import React, { useState, useMemo } from 'react';
import {
    CheckSquare, Plus, Search,
    Calendar as CalendarIcon, Flag, Clock, CheckCircle2,
    Trash2, ChevronRight, ChevronDown, LayoutGrid, List, AlertCircle,
    AlignLeft, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCalendar } from '../../hooks/useCalendar';
import { formatDate } from '../../utils';

const ToDoView = ({ user }) => {
    const {
        todos,
        addTodo,
        updateTodo,
        toggleTodo,
        deleteTodo
    } = useCalendar(user);

    const [searchQuery, setSearchQuery] = useState('');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [editingTodo, setEditingTodo] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({
        Delayed: true,
        Today: true,
        'This week': true,
        'This month': true,
        Upcoming: true
    });
    const [notification, setNotification] = useState(null);
    const [completingId, setCompletingId] = useState(null);

    const groups = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const todayEnd = new Date(now);
        todayEnd.setHours(23, 59, 59, 999);

        const weekEnd = new Date(now);
        weekEnd.setDate(now.getDate() + 7);

        const monthEnd = new Date(now);
        monthEnd.setMonth(now.getMonth() + 1);

        const categorized = {
            Delayed: { color: '#eb1616ff', tasks: [] }, // Red
            Today: { color: '#3b82f6', tasks: [] },   // Blue
            'This week': { color: '#b6a4e7ff', tasks: [] }, // Green
            'This month': { color: '#eab308', tasks: [] }, // Yellow
            Upcoming: { color: '#28e156ff', tasks: [] }  // Blue
        };

        const filtered = todos.filter(t => !t.completed && (
            t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()))
        ));

        filtered.forEach(todo => {
            const dueDate = new Date(todo.dueDate);
            if (dueDate < now) categorized.Delayed.tasks.push(todo);
            else if (dueDate <= todayEnd) categorized.Today.tasks.push(todo);
            else if (dueDate <= weekEnd) categorized['This week'].tasks.push(todo);
            else if (dueDate <= monthEnd) categorized['This month'].tasks.push(todo);
            else categorized.Upcoming.tasks.push(todo);
        });

        return categorized;
    }, [todos, searchQuery]);

    const handleSaveTask = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const todoData = {
            title: formData.get('title'),
            priority: formData.get('priority'),
            dueDate: formData.get('dueDate') ? new Date(formData.get('dueDate')) : new Date(),
            description: formData.get('description'),
            completed: editingTodo ? editingTodo.completed : false
        };

        if (editingTodo) {
            updateTodo(editingTodo.id, todoData);
        } else {
            addTodo(todoData);
        }

        setShowDetailModal(false);
        setEditingTodo(null);
    };

    const hasTasks = todos.some(t => !t.completed);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#ffffff',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Header matching image 1 */}
            <div style={{
                padding: '32px 40px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h1 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#2563eb', // Blue title from image
                    margin: 0
                }}>
                    My Tasks
                </h1>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#f8fafc',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        width: '240px'
                    }}>
                        <Search size={16} color="#94a3b8" />
                        <input
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ border: 'none', background: 'transparent', outline: 'none', marginLeft: '8px', width: '100%', fontSize: '0.9rem' }}
                        />
                    </div>
                </div>
            </div>

            {/* Content area */}
            <div className="scrollbar-hide" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '0 40px 100px'
            }}>
                {!hasTasks ? (
                    <div style={{
                        height: '60%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8'
                    }}>
                        <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>No tasks yet. Add one below!</p>
                    </div>
                ) : (
                    Object.entries(groups).map(([name, group]) => (
                        group.tasks.length > 0 && (
                            <div key={name} style={{ marginBottom: '24px' }}>
                                <div
                                    onClick={() => setExpandedGroups(prev => ({ ...prev, [name]: !prev[name] }))}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '12px 0',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f1f5f9'
                                    }}
                                >
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                        {expandedGroups[name] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </div>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: group.color }} />
                                    <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>{name}</span>
                                </div>

                                <AnimatePresence>
                                    {expandedGroups[name] && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            {group.tasks.map(todo => (
                                                <div
                                                    key={todo.id}
                                                    onClick={() => {
                                                        setEditingTodo(todo);
                                                        setShowDetailModal(true);
                                                    }}
                                                    style={{
                                                        padding: '14px 0 14px 44px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        borderBottom: '1px solid #f8fafc',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                                        <div
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                setCompletingId(todo.id);
                                                                setNotification('Task Checked!');

                                                                // Brief delay for the visual feedback
                                                                setTimeout(async () => {
                                                                    await toggleTodo(todo);
                                                                    setCompletingId(null);
                                                                    setTimeout(() => setNotification(null), 2000);
                                                                }, 400);
                                                            }}
                                                            style={{
                                                                color: completingId === todo.id ? '#22c55e' : '#94a3b8',
                                                                transition: 'color 0.2s',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <CheckCircle2 size={18} fill={completingId === todo.id ? '#22c55e22' : 'transparent'} />
                                                        </div>
                                                        <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#475569' }}>
                                                            {todo.title}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{
                                                            padding: '4px 8px',
                                                            background: '#f1f5f9',
                                                            borderRadius: '6px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600',
                                                            color: todo.priority === 'High' ? '#ef4444' : '#64748b'
                                                        }}>
                                                            {todo.priority}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#3b82f6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700' }}>
                                                                {user?.name?.substring(0, 1) || 'K'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )
                    ))
                )}
            </div>

            {/* Floating Action Button matching image 1 */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                    setEditingTodo(null);
                    setShowDetailModal(true);
                }}
                style={{
                    position: 'absolute',
                    bottom: '40px',
                    right: '40px',
                    padding: '14px 28px',
                    backgroundColor: '#0070f3', // Blue from image 1
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: '0 10px 25px -5px rgba(0, 112, 243, 0.4)',
                    cursor: 'pointer'
                }}
            >
                <Plus size={20} strokeWidth={3} />
                <span>New Task</span>
            </motion.button>

            {/* Task Detail Modal matching image 2 */}
            <AnimatePresence>
                {showDetailModal && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '20px'
                    }}>
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            style={{
                                width: '100%',
                                maxWidth: '580px', // Wider modal as per image 2
                                background: 'white',
                                borderRadius: '20px',
                                padding: '32px',
                                boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.25)'
                            }}
                        >
                            <div style={{ marginBottom: '24px' }}>
                                <X
                                    size={24}
                                    style={{ cursor: 'pointer', color: '#94a3b8' }}
                                    onClick={() => setShowDetailModal(false)}
                                />
                            </div>

                            <form onSubmit={handleSaveTask}>
                                <input
                                    name="title"
                                    defaultValue={editingTodo?.title || ''}
                                    placeholder="Task Title"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px 0',
                                        fontSize: '1.75rem',
                                        fontWeight: '700',
                                        border: 'none',
                                        borderBottom: '2px solid #f1f5f9',
                                        outline: 'none',
                                        color: '#1e293b',
                                        marginBottom: '24px'
                                    }}
                                />

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    marginBottom: '32px',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.9rem' }}>
                                        <span>in</span>
                                        <div style={{
                                            padding: '6px 12px',
                                            background: '#f0f7ff',
                                            color: '#2563eb',
                                            borderRadius: '8px',
                                            fontWeight: '600'
                                        }}>
                                            Personal tasks
                                        </div>
                                    </div>

                                    <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Flag size={16} color="#94a3b8" />
                                        <select
                                            name="priority"
                                            defaultValue={editingTodo?.priority || 'Medium'}
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                padding: '4px 8px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                color: '#475569',
                                                background: 'white',
                                                outline: 'none'
                                            }}
                                        >
                                            <option value="Low">! Low</option>
                                            <option value="Medium">! Medium</option>
                                            <option value="High">! High</option>
                                        </select>
                                    </div>

                                    <div style={{ width: '1px', height: '24px', background: '#e2e8f0' }} />

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Due:</span>
                                        <input
                                            name="dueDate"
                                            type="date"
                                            defaultValue={editingTodo?.dueDate ? new Date(editingTodo.dueDate).toISOString().split('T')[0] : ''}
                                            style={{
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                padding: '4px 8px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                color: '#475569',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '40px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#1e293b' }}>
                                        <AlignLeft size={18} />
                                        <span style={{ fontWeight: '700', fontSize: '1rem' }}>Description</span>
                                    </div>
                                    <textarea
                                        name="description"
                                        defaultValue={editingTodo?.description || ''}
                                        placeholder="Add a more detailed description..."
                                        style={{
                                            width: '100%',
                                            height: '140px',
                                            padding: '16px',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            resize: 'none',
                                            fontSize: '0.95rem',
                                            color: '#334155',
                                            outline: 'none',
                                            backgroundColor: '#fcfcfc'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '16px' }}>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px 32px',
                                            backgroundColor: '#2563eb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontWeight: '700',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Save Task
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowDetailModal(false)}
                                        style={{
                                            padding: '12px 32px',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontWeight: '700',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancel
                                    </button>

                                    {editingTodo && (
                                        <button
                                            type="button"
                                            onClick={() => { deleteTodo(editingTodo.id); setShowDetailModal(false); }}
                                            style={{ marginLeft: 'auto', padding: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    )}
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
                {/* Notification Toast */}
                <AnimatePresence>
                    {notification && (
                        <motion.div
                            initial={{ opacity: 0, y: 50, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.9 }}
                            style={{
                                position: 'fixed',
                                bottom: '100px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#1e293b',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '12px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                zIndex: 3000,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: '600',
                                fontSize: '0.9rem'
                            }}
                        >
                            <CheckCircle2 size={18} color="#22c55e" />
                            {notification}
                        </motion.div>
                    )}
                </AnimatePresence>
            </AnimatePresence>
        </div>
    );
};

export default ToDoView;
